import React, { useEffect, useState } from "react";
import { Loader2, Send, Zap } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCamp } from "@/context/SettingsContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { apiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";

const splitAddrs = (s) => s.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);

export default function Composer({ mode = "new", orig = null, to: presetTo = "", initialBody = "", onClose }) {
  const { t } = useLang();
  const { user } = useAuth();
  const { camp } = useCamp();
  const [templates, setTemplates] = useState([]);
  const [tplId, setTplId] = useState("");
  useEffect(() => { api.get("/mailbox/templates").then(({ data }) => setTemplates(data)).catch(() => {}); }, []);
  const [to, setTo] = useState(mode === "reply" ? (orig?.reply_to?.email || orig?.from?.email || "") : presetTo);
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState(
    mode === "reply" ? `Re: ${(orig?.subject || "").replace(/^(re|aw|fwd?):\s*/i, "")}`
      : mode === "forward" ? `Fwd: ${(orig?.subject || "").replace(/^fwd?:\s*/i, "")}` : "");
  const [body, setBody] = useState(initialBody);
  const [busy, setBusy] = useState(false);

  const send = async () => {
    setBusy(true);
    try {
      await api.post("/mailbox/send", {
        to: splitAddrs(to), cc: splitAddrs(cc), subject, body, mode,
        reply_uid: orig?.uid || null, reply_folder: orig?.folder || "inbox", guest_id: orig?.guest?.guest_id || null,
      });
      toast.success(t("mb_sent_ok"));
      onClose(true);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const staff = user?.nickname || user?.name || user?.email || "";

  // Sjabloon invullen: placeholders worden vervangen met wat we van de gast/reservering weten
  const applyTemplate = (id) => {
    const tpl = templates.find((x) => x.id === id);
    if (!tpl) return;
    setTplId(id);
    const g = orig?.guest;
    const res = g?.reservations?.[0];
    const guestName = g?.guest_name || orig?.from?.name || orig?.reply_to?.name || "";
    const fill = (str) => str
      .replaceAll("{naam}", guestName).replaceAll("{camping}", camp?.camp_name || "")
      .replaceAll("{aankomst}", res?.arrival || "…").replaceAll("{vertrek}", res?.departure || "…")
      .replaceAll("{plaats}", res?.pitch_label || "…").replaceAll("{medewerker}", staff);
    setBody(fill(tpl.body));
    if (mode === "new" || !subject.trim()) setSubject(fill(tpl.subject));
  };
  const guestLang = orig?.guest?.language || "";
  const sorted = [...templates].sort((a, b) => (a.lang === guestLang ? -1 : 0) - (b.lang === guestLang ? -1 : 0));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="sm:max-w-2xl bg-background" data-testid="mail-composer">
        <DialogHeader><DialogTitle className="font-display">{t(mode === "reply" ? "mb_reply" : mode === "forward" ? "mb_forward" : "mb_new")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1"><Label className="text-xs">{t("mb_to")}</Label><Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="gast@voorbeeld.be" data-testid="compose-to" /></div>
          <div className="space-y-1"><Label className="text-xs">CC</Label><Input value={cc} onChange={(e) => setCc(e.target.value)} data-testid="compose-cc" /></div>
          <div className="space-y-1"><Label className="text-xs">{t("mb_subject")}</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="compose-subject" /></div>
          {templates.length > 0 && (
            <div className="space-y-1">
              <Label className="text-xs flex items-center gap-1"><Zap className="h-3 w-3" />{t("mb_quick_reply")}</Label>
              <Select value={tplId} onValueChange={applyTemplate}>
                <SelectTrigger data-testid="compose-template"><SelectValue placeholder={t("mb_pick_template")} /></SelectTrigger>
                <SelectContent>{sorted.map((x) => <SelectItem key={x.id} value={x.id} data-testid={`compose-template-${x.id}`}><span className="font-mono text-[10px] mr-2">{x.lang.toUpperCase()}</span>{x.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1">
            <Label className="text-xs">{t("mb_message")}</Label>
            <Textarea rows={10} value={body} onChange={(e) => setBody(e.target.value)} data-testid="compose-body" />
            <p className="text-[11px] text-muted-foreground">{t("mb_signature_hint").replace("{name}", staff)}{orig && mode === "reply" ? ` · ${t("mb_quote_hint")}` : ""}{orig && mode === "forward" ? ` · ${t("mb_forward_hint")}` : ""}</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)} data-testid="compose-cancel">{t("cancel")}</Button>
          <Button onClick={send} disabled={busy || !to.trim() || !subject.trim() || !body.trim()} data-testid="compose-send">
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}{t("mb_send")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
